import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { endOfDay } from 'date-fns'
import { fromZonedTime } from 'date-fns-tz'
import { debounce } from 'lodash'

import FeaturedTable from '../FeaturedTable/FeaturedTable'
import PaginationFooter from '../PaginationFooter/PaginationFooter'
import ConfirmPopup from '../Popups/ConfirmPopup'
import {
  selectInspections,
  selectInspectionsPagination,
  fetchScheduledInspections,
  bulkDeleteScheduledInspections,
  setInspectionsPagination,
} from '../../reducers/inspectionsReducer'
import { selectActiveBuilding } from '../../reducers/buildingSelectorReducer'
import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import API from '../../api/api'

import InternalInspectionsAnalytics from './InternalInspectionsAnalytics'
import { dashboardColumns, INSPECTION_STATUS } from './helpers'
import InternalInspectionsToolbar from './InternalInspectionsToolbar'

const InternalInspectionsDashboard = ({
  building,
  inspections,
  pagination,
  fetchScheduledInspections = () => {},
  bulkDeleteScheduledInspections = () => {},
  setInspectionsPagination = () => {},
}) => {
  const { t } = useTranslation(['internal-inspections', 'common'])
  const { showAnalytics } = useOutletContext()
  const [isLoading, setSiteLoading] = useState(false)
  const [paginationItemsCount, setPaginationItemsCount] = useState(0)
  const [selectedRowsIds, setSelectedRowsIds] = useState([])
  const [clearSelectedRows, setClearSelectedRows] = useState(() => () => {})
  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [isDeleteInspectionPopupOpen, setIsDeleteInspectionPopupOpen] = useState(false)
  const [isNotificationPopupOpen, setIsNotificationPopupOpen] = useState(false)
  const [isBulkDeleteLoading, setIsBulkDeleteLoading] = useState(false)
  const [isBulkNotificationLoading, setIsBulkNotificationLoading] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const navigate = useNavigate()

  const { currentPage, pageSize, search, currentStatus, currentRange } = pagination

  const handleSetPageChange = (pageNum) => {
    setInspectionsPagination({ currentPage: pageNum })
  }

  const handleSearch = (e) => {
    setInspectionsPagination({ search: e.target.value })
  }

  const handleStatusChange = (status) => {
    setInspectionsPagination({ currentStatus: status, currentPage: 1 })
  }

  const handleDateRangeChange = ([startDate, endDate]) => {
    setInspectionsPagination({
      currentRange: { startDate, endDate },
      currentPage: 1,
    })
  }

  const handlePageSizeChange = (newPageSize) => {
    setInspectionsPagination({ pageSize: newPageSize, currentPage: 1 })
  }

  const onDeleteInspection = async () => {
    if (!building?.id) return

    setIsBulkDeleteLoading(true)

    try {
      await bulkDeleteScheduledInspections(selectedRowsIds)
      setIsDeleteInspectionPopupOpen(false)
      clearSelectedRows()

      const totalCount = await fetchInspections()
      const lastPage = Math.max(1, Math.ceil(totalCount / pageSize))

      // Deleting every row of the last page leaves the current page out of range
      if (currentPage > lastPage) {
        setSiteLoading(true)
        setInspectionsPagination({ currentPage: lastPage })
      }

      displaySnackbar(t('internal-inspections:dashboard.deletedSuccessfully'))
    } catch {
      displaySnackbar(t('internal-inspections:dashboard.errorDeleting'), true)
    }

    setIsBulkDeleteLoading(false)
  }

  const onSendReminder = async () => {
    setIsBulkNotificationLoading(true)
    try {
      await API.notifyScheduledInspections(selectedRowsIds)
      setIsNotificationPopupOpen(false)
      clearSelectedRows()
      displaySnackbar(t('internal-inspections:dashboard.reminderSentSuccessfully'))
    } catch {
      displaySnackbar(t('internal-inspections:dashboard.errorSendingReminder'), true)
    }
    setIsBulkNotificationLoading(false)
  }

  const onRowSelect = useCallback((ids) => setSelectedRowsIds(ids), [])

  const fetchInspections = async (query) => {
    if (!building?.id) return

    setSiteLoading(true)

    // Only reset to page 1 if we have an actual search query that's different from current search
    const isNewSearch = query !== undefined && query !== search

    const requestData = {
      buildingId: building.id,
      page: isNewSearch ? 1 : currentPage,
      limit: pageSize,
      search: query !== undefined ? query : search,
      status: currentStatus.value,
      startDate: currentRange.startDate
        ? fromZonedTime(currentRange.startDate, building.timezone).getTime()
        : undefined,
      endDate: currentRange.endDate
        ? fromZonedTime(endOfDay(currentRange.endDate), building.timezone).getTime()
        : undefined,
    }

    if (isNewSearch) {
      setInspectionsPagination({ currentPage: 1 })
    }

    const count = await fetchScheduledInspections(requestData)
      .then((total) => {
        setPaginationItemsCount(total)
        return total
      })
      .catch(({ message }) => displaySnackbar(message, true))

    setSiteLoading(false)

    return count
  }

  const memoizedColumns = useMemo(
    () => dashboardColumns({ timezone: building ? building.timezone : '' }, t),
    [building, t],
  )

  const openEdit = (id) => navigate(`/dashboard/building-dashboard/internal-inspections/edit/${id}`)

  const openDetails = (id) =>
    navigate(`/dashboard/building-dashboard/internal-inspections/details/${id}`)

  const onRowClick = ({ original }) => {
    if (original.id) {
      if ([INSPECTION_STATUS.STARTED, INSPECTION_STATUS.COMPLETED].includes(original.status)) {
        openDetails(original.id)
      } else {
        openEdit(original.id)
      }
    }
  }

  const onEdit = () => {
    if (selectedRowsIds.length === 1) {
      openEdit(selectedRowsIds[0])
    }
  }

  const debouncedFetch = debounce(fetchInspections, 800)

  useEffect(() => {
    if (building) {
      fetchInspections()
    }
  }, [building, currentStatus, currentRange, currentPage, pageSize])

  useEffect(() => {
    if (prevSearchValue !== search) {
      window.addEventListener('keydown', debouncedFetch(search))
    }
    setPrevSearchValue(search)
    return () => {
      window.removeEventListener('keydown', debouncedFetch)
    }
  }, [search, debouncedFetch])

  return (
    <React.Fragment>
      {showAnalytics && <InternalInspectionsAnalytics buildingId={building?.id} />}
      <div
        className={`ctp-internal-inspections__container ${isLoading ? 'is-loading' : ''} ${
          !showAnalytics ? 'ctp-internal-inspections__container__full-height' : ''
        }`}
      >
        <InternalInspectionsToolbar
          onEdit={onEdit}
          onDelete={() => setIsDeleteInspectionPopupOpen(true)}
          onSendReminder={() => setIsNotificationPopupOpen(true)}
          search={search}
          onSearch={handleSearch}
          isLoading={isLoading}
          selectedRows={selectedRowsIds}
          componentOptions={{
            currentStatus,
            currentRange,
            onChangeStatus: handleStatusChange,
            onChangeDateRange: handleDateRangeChange,
          }}
        />
        <div className="internal-inspections-list-table">
          {inspections.length ? (
            <FeaturedTable
              data={inspections}
              columns={memoizedColumns}
              onChangeSelectedRows={onRowSelect}
              setClearSelectedRows={setClearSelectedRows}
              onRowClick={onRowClick}
            />
          ) : !isLoading ? (
            <div className="no-results">
              {t('internal-inspections:dashboard.noInspectionsFound')}
            </div>
          ) : null}
        </div>
        {inspections.length ? (
          <PaginationFooter
            showOnPage={pageSize}
            pageNumber={currentPage}
            paginationItemsCount={paginationItemsCount}
            setSiteLoading={setSiteLoading}
            setShowOnPage={handlePageSizeChange}
            setPageNumber={handleSetPageChange}
          />
        ) : null}
      </div>
      <ConfirmPopup
        isOpen={isDeleteInspectionPopupOpen}
        isLoading={isBulkDeleteLoading}
        cancelCallback={() => setIsDeleteInspectionPopupOpen(false)}
        confirmCallback={onDeleteInspection}
        cancelText={t('common:actions.cancel')}
        confirmText={t('internal-inspections:dashboard.confirmDeleteInspections')}
        title={t('internal-inspections:dashboard.confirmationTitle')}
        body={t('internal-inspections:dashboard.confirmDeleteBody')}
      />
      <ConfirmPopup
        isOpen={isNotificationPopupOpen}
        isLoading={isBulkNotificationLoading}
        cancelCallback={() => setIsNotificationPopupOpen(false)}
        confirmCallback={() => !isBulkNotificationLoading && onSendReminder()}
        cancelText={t('common:actions.back')}
        confirmText={t('internal-inspections:dashboard.confirmSendReminder')}
        title={t('internal-inspections:dashboard.cautionTitle')}
        body={t('internal-inspections:dashboard.confirmSendReminderBody')}
        className="is-caution"
      />
    </React.Fragment>
  )
}

InternalInspectionsDashboard.propTypes = {
  building: PropTypes.object,
  inspections: PropTypes.array,
  pagination: PropTypes.object,
  fetchScheduledInspections: PropTypes.func,
  bulkDeleteScheduledInspections: PropTypes.func,
  setInspectionsPagination: PropTypes.func,
}

export default connect(
  (state) => ({
    inspections: selectInspections(state),
    pagination: selectInspectionsPagination(state),
    building: selectActiveBuilding(state),
  }),
  {
    fetchScheduledInspections,
    bulkDeleteScheduledInspections,
    setInspectionsPagination,
  },
)(InternalInspectionsDashboard)
